/**
 * /auth/signup — Email + password signup, as a TWO-STEP flow.
 *
 * 2026-05-20 (B-01 / B-02 + F02-A-022 closure): magic-link-only signup was
 * dropped on Paul's call as Persona 06. New accounts now set a password
 * up-front via supabase.auth.signUp. After Supabase emails the address
 * confirmation, the user is routed to /auth/check-email; once they confirm
 * and return, they sign in via /auth/login with the password they just set.
 *
 * 2026-06-21 (persona chooser): a new account picks who they are up-front —
 * Individual (administer their own/family trust → the B2C welcome wizard) or
 * Firm (a fiduciary practice → the document-first firm bootstrap). The choice
 * rides along on `options.data.account_type` ('individual' | 'firm') so
 * /auth/callback can route the confirmed, no-tenant session into the matching
 * EXISTING onboarding.
 *
 * 2026-06-22 (TWO-STEP refactor): the chooser and the details now live on
 * SEPARATE in-component steps (no new route — `step` is local state):
 *   - Step 1 — persona choice: two large selectable cards ("I am an individual"
 *     / "I am a firm"). Selecting a card sets the persona AND advances to Step 2.
 *     The cards remain a WAI-ARIA radiogroup (arrow/space/enter, aria-checked).
 *   - Step 2 — details: the EXISTING base fields (first name, surname, email,
 *     password, confirm) for BOTH personas. A Back link returns to Step 1. The
 *     FIRM persona additionally collects "Company name" + "Your role in the
 *     company" — both required on the firm path; they flow to
 *     user_metadata.company_name / job_title and on to the firm bootstrap.
 *
 * Customer-specific firm names are never hardcoded here — public signup
 * must remain audience-neutral. (A partner-firm-clients disclaimer that
 * once sat below Step 1 was removed 2026-06-22 on the owner's call — it
 * confused individuals signing up for themselves.)
 */

import { useEffect, useRef, useState } from 'react';
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { AuthLayout } from '../../components/layouts/auth-layout';
import { supabase } from '../../lib/supabase';
import { mapSupabaseAuthError, isTransientAuthError } from '../../lib/auth-errors';
import { PASSWORD_MIN_LENGTH, passwordProblem } from '../../lib/password-policy';
import { PasswordRequirements } from '../../components/auth/password-requirements';
import { useTenantBrandingBySlug } from '../../lib/api';
import { resolveTenantSlugFromHost } from '../../lib/tenant';
import {
  resolveSignupFirmSlug,
  persistPendingFirmSlug,
  buildSignupRedirectUrl,
} from '../../lib/firm-self-signup';
import { stashPendingAuthEmail } from '../../lib/pending-auth-email';

export const Route = createFileRoute('/auth/signup')({
  component: SignupPage,
});

/** Ties the live rule checklist to the password input's aria-describedby. */
const PASSWORD_RULES_ID = 'signup-password-requirements';

/**
 * Client Model B firm context for the signup page. Resolves the firm slug
 * (`?firm=<slug>` wins, else the host subdomain), looks up the public branding,
 * and returns a branded context ONLY when the firm has opted in to self-signup
 * (`openSignup.enabled`). Everything else (no slug, 404, opt-in off) returns
 * null so the page renders the normal global signup, unchanged.
 *
 * The branding decoration (logo/wordmark/footer) is already applied by
 * AuthLayout via the same slug on a tenant SUBDOMAIN; this hook additionally
 * drives the COPY + the self-join wiring, and covers the `?firm=` case on the
 * apex host (where AuthLayout's host-only resolution wouldn't see the slug).
 */
function useSignupFirmContext(): {
  firmSlug: string;
  firmName: string | null;
  billingOwner: 'firm' | 'client';
} | null {
  const hostSlug =
    typeof window !== 'undefined'
      ? resolveTenantSlugFromHost(
          window.location.host,
          new URLSearchParams(window.location.search),
        )
      : null;
  const search =
    typeof window !== 'undefined' ? new URLSearchParams(window.location.search) : null;
  const firmSlug = resolveSignupFirmSlug(search, hostSlug);
  const query = useTenantBrandingBySlug(firmSlug);
  if (!firmSlug) return null;
  const data = query.data;
  // Only an explicit opt-in turns the page branded. Absent openSignup (older /
  // cached response) or enabled:false → fall back to the normal signup.
  if (!data?.openSignup?.enabled) return null;
  return {
    firmSlug,
    firmName: data.branding.firmName?.trim() || null,
    billingOwner: data.openSignup.billingOwner,
  };
}

type AccountType = 'individual' | 'firm';
type Step = 'persona' | 'details';

function SignupPage() {
  // Client Model B: a firm-branded self-signup context, present only when the
  // firm opted in. When present the page is BRANDED and the persona chooser is
  // skipped — a self-joining client is always an individual joining the firm.
  const firmContext = useSignupFirmContext();
  const isFirmSelfSignup = firmContext !== null;

  // Step 1 starts unchosen; selecting a card sets the persona AND advances. We
  // remember the chosen persona so a Back → re-enter keeps the radiogroup
  // selection, and the submit handler reads it to shape the metadata. For a
  // firm self-signup there is no chooser: start on the details step as an
  // 'individual' joining the firm.
  //
  // NB the initial value is OPTIMISTIC: on a WARM branding cache `firmContext`
  // is already resolved at mount, so we open straight on the branded details
  // step. On a COLD load the branding query is still pending here, so
  // `isFirmSelfSignup` is false → these init to the persona chooser. The
  // re-sync effect below recovers that case once the query settles (BUG 3).
  const [step, setStep] = useState<Step>(isFirmSelfSignup ? 'details' : 'persona');
  const [accountType, setAccountType] = useState<AccountType | null>(
    isFirmSelfSignup ? 'individual' : null,
  );
  // True once the user has interacted with the persona chooser (picked a card
  // or stepped Back to it). It gates the cold-load re-sync so we NEVER yank a
  // user who is deliberately on the (non-branded) persona chooser.
  const personaTouched = useRef(false);
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [companyName, setCompanyName] = useState('');
  const [jobTitle, setJobTitle] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // ── CHK-01: the existing-account dead end ─────────────────────────────────
  // Signing up with an address that already has an account is a real outcome
  // with a real person behind it, and until now it ended in a sentence. The
  // address that came back as taken, held so the offer below can act on it
  // without asking the person to type it a second time. Cleared the moment the
  // email field changes, so the offer can never act on a stale address.
  const [takenEmail, setTakenEmail] = useState<string | null>(null);
  const [recovery, setRecovery] = useState<'idle' | 'sending' | 'sent'>('idle');
  const [recoveryError, setRecoveryError] = useState<string | null>(null);
  const navigate = useNavigate();

  /** Forget an existing-account offer that no longer matches what is typed. */
  function clearTakenEmail() {
    setTakenEmail(null);
    setRecovery('idle');
    setRecoveryError(null);
  }

  function choosePersona(next: AccountType) {
    personaTouched.current = true;
    setAccountType(next);
    setError(null);
    clearTakenEmail();
    setStep('details');
  }

  function backToPersona() {
    if (busy) return;
    // The user deliberately returned to the chooser — lock out the cold-load
    // re-sync so a late branding resolution can't bounce them off it.
    personaTouched.current = true;
    setError(null);
    clearTakenEmail();
    setStep('persona');
  }

  /**
   * CHK-01, the half that was never built: on an existing account, SEND
   * something real.
   *
   * What it sends is a password-reset, not a magic link, and that is not a
   * shortcut — passwordless sign-in was dropped from Elreign on purpose and the
   * auth-mail hook refuses a `magiclink` action outright
   * (`backend/src/functions/auth/email-hook.ts`, 410 MAGICLINK_DISABLED). The
   * `recovery` action IS rendered by that same hook, so this is a real message
   * on the firm's own branding, and it lands the person on
   * /auth/reset-password with a way back into the account they already have.
   *
   * A failure is SHOWN. The whole row exists because a screen once claimed a
   * send that had not happened.
   */
  async function sendRecoveryToTakenEmail() {
    if (takenEmail === null || recovery === 'sending') return;
    setRecovery('sending');
    setRecoveryError(null);
    try {
      const { error: sendErr } = await supabase.auth.resetPasswordForEmail(takenEmail, {
        redirectTo: `${window.location.origin}/auth/reset-password`,
      });
      if (sendErr) throw new Error(sendErr.message ?? '');
      setRecovery('sent');
    } catch (e: unknown) {
      setRecovery('idle');
      // Deliberately NOT `mapSupabaseAuthError`: its contexts are sign-in and
      // sign-up, and its fallback for an unknown message would say "Sign-in
      // failed" about an email that did not send. Two honest sentences instead,
      // because Supabase throttles this endpoint hard and a person who has just
      // been told to wait can act on that.
      const raw = (e instanceof Error ? e.message : '').toLowerCase();
      setRecoveryError(
        raw.includes('rate limit') || raw.includes('too many')
          ? 'Too many emails have been asked for on that address just now. Wait a minute and try again.'
          : 'We could not send that email just now. Try again in a moment, or use “Forgot password” on the sign-in page.',
      );
    }
  }

  // BUG 3 re-sync: a client following a firm's `/auth/signup?firm=<slug>` link
  // on a COLD first visit mounts before the async branding query resolves, so
  // `isFirmSelfSignup` is false at mount and the page opens on the GENERIC
  // persona chooser. When the query later settles to an opted-in firm, advance
  // to the branded client details step — but ONLY while the user is still on
  // the untouched initial chooser (hasn't manually picked a persona or stepped
  // Back). This never fires on a normal signup (branding never becomes a
  // self-signup context) and is idempotent: once `step` leaves 'persona' the
  // guard is false, so it can't loop.
  useEffect(() => {
    if (isFirmSelfSignup && step === 'persona' && accountType === null && !personaTouched.current) {
      setStep('details');
      setAccountType('individual');
    }
  }, [isFirmSelfSignup, step, accountType]);

  const isFirm = accountType === 'firm';

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (busy) return;
    // Defensive: details step is only reachable with a persona chosen.
    const persona: AccountType = accountType ?? 'individual';
    setBusy(true);
    setError(null);
    const fn = firstName.trim();
    const ln = lastName.trim();
    if (!fn || !ln) {
      setBusy(false);
      setError('Enter your first name and surname to continue.');
      return;
    }
    const trimmed = email.trim();
    if (!trimmed) {
      setBusy(false);
      setError('Enter your email address to continue.');
      return;
    }
    if (!/.+@.+\..+/.test(trimmed)) {
      setBusy(false);
      setError('Email format is name@example.co.za.');
      return;
    }
    // SGN-02: the same four rules the checklist under the field draws, from the
    // same list. The message names what is still missing, never "invalid".
    const pwProblem = passwordProblem(password);
    if (pwProblem !== null) {
      setBusy(false);
      setError(pwProblem);
      return;
    }
    if (password !== confirm) {
      setBusy(false);
      setError('The two password fields do not match.');
      return;
    }
    // Firm-only fields are required on the firm path.
    const company = companyName.trim();
    const role = jobTitle.trim();
    if (persona === 'firm') {
      if (!company) {
        setBusy(false);
        setError('Enter your company name to continue.');
        return;
      }
      if (!role) {
        setBusy(false);
        setError('Enter your role in the company to continue.');
        return;
      }
    }
    const fullName = `${fn} ${ln}`;
    // Stored on the Supabase user_metadata. `name` is what the backend
    // users-me fallback + the onboarding welcome greeting read (the greeting
    // reads it client-side from the session, since /users/me 403s until a
    // tenant is assigned during onboarding). `account_type` is the persona —
    // /auth/callback reads it off session.user.user_metadata to route into the
    // matching onboarding. FIRM ONLY additionally carries company_name +
    // job_title, which /onboarding/firm pre-fills + threads through to onboard.
    const data: Record<string, string> = {
      name: fullName,
      first_name: fn,
      last_name: ln,
      account_type: persona,
    };
    if (persona === 'firm') {
      data['company_name'] = company;
      data['job_title'] = role;
    }
    // Client Model B: stamp the firm slug on the user_metadata too (a belt-and-
    // braces signal alongside the persisted slug + the redirect query), and
    // persist it so it survives the email-confirm round-trip even if the user
    // confirms on the same device. The callback reads it to fire self-join.
    if (firmContext) {
      data['self_join_firm_slug'] = firmContext.firmSlug;
      persistPendingFirmSlug(firmContext.firmSlug);
    }
    const signUpArgs = {
      email: trimmed,
      password,
      options: {
        // For a branded self-signup the slug rides the magic link so a cross-
        // device confirm still recovers it; a normal signup keeps the bare URL.
        emailRedirectTo: buildSignupRedirectUrl(
          window.location.origin,
          firmContext ? firmContext.firmSlug : null,
        ),
        data,
      },
    };

    // BUG-1: the first signup attempt can fail transiently (a cold backend / a
    // network blip) and then succeed on a retry. Attempt once; on a TRANSIENT
    // failure ONLY, retry exactly once. Never on a terminal error (already
    // registered / invalid / throttled) — that would mask an actionable message
    // or worsen a throttle. supabase.auth.signUp is idempotent for an unconfirmed
    // email, so the retry can't double-create an account. A hard transport
    // failure can REJECT the promise rather than return `{ error }`; normalise
    // either into an error shape so the same transient check + mapping applies.
    type SignUpOutcome = {
      data?: { user?: { identities?: unknown[] | null } | null } | null;
      error?: { message?: string | null; status?: number } | null;
    };
    const attemptSignUp = async (): Promise<SignUpOutcome> => {
      try {
        return (await supabase.auth.signUp(signUpArgs)) as SignUpOutcome;
      } catch (thrown) {
        const err = (thrown ?? {}) as { message?: string; status?: number };
        return { error: { message: err.message ?? 'network error', status: err.status } };
      }
    };

    let outcome = await attemptSignUp();
    if (outcome.error && isTransientAuthError(outcome.error)) {
      outcome = await attemptSignUp();
    }
    const { data: signUpData, error: signUpErr } = outcome;
    setBusy(false);
    if (signUpErr) {
      // BUG-2: signup context → a generic failure reads "create your account",
      // not the sign-in verb.
      setError(mapSupabaseAuthError(signUpErr.message, 'signup'));
      return;
    }
    // ALREADY-REGISTERED guard. Supabase's anti-enumeration behaviour: signing
    // up with an email that already has a CONFIRMED account returns NO error,
    // but a user object whose `identities` array is EMPTY (it deliberately
    // won't resend a confirmation for a live account, so it can't leak which
    // emails exist). Without this check we'd send the user to "check your
    // email" for a confirmation that never arrives (exactly what happened to a
    // returning user). Stop here and point them at sign-in instead.
    const identities = signUpData?.user?.identities;
    if (identities && identities.length === 0) {
      setError('An account with that email already exists, so nothing was sent.');
      // CHK-01: and then OFFER something, rather than ending on a sentence.
      setTakenEmail(trimmed);
      setRecovery('idle');
      setRecoveryError(null);
      return;
    }
    // The addressee travels in sessionStorage, NOT the URL — an email address is
    // personal information and a query string leaks it into history, referrers
    // and access logs (2026-08-25, caught on Paul's recorded prod walkthrough).
    stashPendingAuthEmail(trimmed);
    void navigate({ to: '/auth/check-email', search: { flow: 'signup' } as never });
  }

  const errorId = 'signup-error';
  const describedBy = error !== null ? errorId : undefined;

  // ── Step 1 — persona choice ────────────────────────────────────────────────
  // Beno feedback (2026-08-01): "we don't have to explain to somebody how to
  // create an account" — dropped the "First, tell us who you are. You can
  // change this on the next step." sub-line; the persona cards below are
  // already self-explanatory. Same move as the 2026-07-15 fix just below
  // (the dropped "Trust administration begins with a verified email…"
  // subtitle): no `sub` prop, so AuthLayout renders its no-sub spacer
  // instead. The persona cards (the actual sign-up mechanism) and the
  // "Already have an account? Sign in" link are untouched.
  if (step === 'persona') {
    return (
      <AuthLayout
        eyebrow="Create account"
        heading="Create your Elreign account."
      >
        <PersonaChooser value={accountType} onChoose={choosePersona} />

        <div
          style={{
            marginTop: 24,
            paddingTop: 20,
            borderTop: '1px solid var(--hairline)',
          }}
        >
          <p style={{ fontSize: 13, color: 'var(--ink-3)', margin: 0, textAlign: 'center' }}>
            Already have an account?{' '}
            <Link to="/auth/login" style={{ color: 'var(--brand-primary, var(--primary-600))', fontWeight: 500 }}>
              Sign in
            </Link>
          </p>
        </div>
      </AuthLayout>
    );
  }

  // ── Step 2 — details (varies by persona) ───────────────────────────────────
  // Client Model B branded copy: name the firm in the heading + reflect who pays.
  const brandedHeading = firmContext
    ? firmContext.firmName
      ? `Create your account with ${firmContext.firmName}.`
      : 'Create your client account.'
    : null;
  // Beno feedback (2026-07-15): the "Trust administration begins with a
  // verified email…" subtitle was dropped as noise above a form whose fields
  // already say what it needs (email + password). Both places this exact
  // line rendered — the branded self-signup client-pays case, and the plain
  // individual details step — now render no subtitle instead.
  const brandedSub = firmContext
    ? firmContext.billingOwner === 'firm'
      ? 'Your firm covers your subscription. Set a verified email and a password to begin.'
      : undefined
    : null;

  // SGN-03: the FIRM sub-line came off with the Back button. "A few details
  // about you and your practice. You can refine everything later." described the
  // form it sat on top of, and cost two lines of a screen Paul was already
  // having to scroll. Same call as the 2026-07-15 one that dropped the other
  // subtitle on this page. The branded CLIENT sub stays, because it says who
  // pays, which no field on the form says.

  return (
    <AuthLayout
      eyebrow={firmContext ? 'Client account' : isFirm ? 'Firm account' : 'Personal account'}
      heading={brandedHeading ?? (isFirm ? 'Tell us about your firm.' : 'Create your account.')}
      sub={brandedSub ?? undefined}
    >
      {/* SGN-03. Back used to sit ABOVE the form, so the screen opened on a way
          out. Paul, M3: "take this back button and put it down here at the
          bottom. And then just move everything up so that I don't need to
          scroll down." It is now the second control in the action row at the
          foot of the form; see the row just above the Terms line. */}
      <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <FieldLabel label="First name" htmlFor="signup-first">
            <input
              id="signup-first"
              type="text"
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              placeholder="First name"
              autoComplete="given-name"
              required
              aria-invalid={error !== null}
              aria-describedby={describedBy}
              disabled={busy}
              style={inputStyle}
            />
          </FieldLabel>
          <FieldLabel label="Surname" htmlFor="signup-last">
            <input
              id="signup-last"
              type="text"
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
              placeholder="Surname"
              autoComplete="family-name"
              required
              aria-invalid={error !== null}
              aria-describedby={describedBy}
              disabled={busy}
              style={inputStyle}
            />
          </FieldLabel>
        </div>

        <FieldLabel label="Email address" htmlFor="signup-email">
          <input
            id="signup-email"
            type="email"
            value={email}
            onChange={(e) => {
              setEmail(e.target.value);
              // CHK-01: the offer below is bound to ONE address. Editing the
              // field retires it, so a click can never send a message to an
              // address the person has already moved on from.
              if (takenEmail !== null) clearTakenEmail();
            }}
            placeholder="you@example.co.za"
            autoComplete="email"
            required
            aria-invalid={error !== null}
            aria-describedby={describedBy}
            disabled={busy}
            style={inputStyle}
          />
        </FieldLabel>

        {/* SGN-03: company + role share a row, the way first name and surname
            already do on the same form. Two full-width fields for two short
            answers cost 80px of the height Paul was scrolling past, and neither
            answer needs the whole card. The labels and the field ids are
            unchanged; only the geometry moved. */}
        {isFirm && (
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <FieldLabel label="Company name" htmlFor="signup-company">
              <input
                id="signup-company"
                type="text"
                value={companyName}
                onChange={(e) => setCompanyName(e.target.value)}
                placeholder="Acacia Fiduciary"
                autoComplete="organization"
                required
                aria-invalid={error !== null}
                aria-describedby={describedBy}
                disabled={busy}
                style={inputStyle}
              />
            </FieldLabel>

            <FieldLabel label="Your role in the company" htmlFor="signup-role">
              <input
                id="signup-role"
                type="text"
                value={jobTitle}
                onChange={(e) => setJobTitle(e.target.value)}
                placeholder="Director"
                autoComplete="organization-title"
                required
                aria-invalid={error !== null}
                aria-describedby={describedBy}
                disabled={busy}
                style={inputStyle}
              />
            </FieldLabel>
          </div>
        )}

        {/* ── SGN-04: two boxes for the same kind of value, drawn the same ────
            Paul, 2026-08-28, walking the deployed build: "the sign up part looks
            strange with the password and confirm password not being the same
            length."

            The cause was the SGN-03 layout this replaces. That pass put the rule
            checklist in the column BESIDE the password field, which bought back
            85px of height and, in doing so, left Password at half the card while
            Confirm password spanned the whole of it. Nothing was wrong with
            either field; they simply stopped looking like the same control.

            The two now share ONE row at equal width, the way First name /
            Surname and Company / Role already do further up this same form, so
            the card reads as three rows of pairs instead of one odd row and a
            slab. The checklist keeps its job and moves under both, spanning the
            row it describes.

            THE CHECKLIST STAYS. Paul did not complain about it and it is what
            SGN-02 was for. It is still rendered here rather than through
            `requirementsId` so it can span the whole row rather than sit inside
            one field's column, and the password field still points
            `aria-describedby` at it, which is the half a screen reader hears.

            Height is close to a wash: a whole field row leaves the form and the
            checklist row arrives in its place. */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <PasswordField
            id="signup-password"
            label="Password"
            value={password}
            onChange={setPassword}
            placeholder="Your password"
            autoComplete="new-password"
            invalid={error !== null}
            describedBy={[PASSWORD_RULES_ID, describedBy].filter(Boolean).join(' ')}
            disabled={busy}
          />
          <PasswordField
            id="signup-confirm"
            label="Confirm password"
            value={confirm}
            onChange={setConfirm}
            placeholder="Re-enter password"
            autoComplete="new-password"
            invalid={error !== null}
            describedBy={describedBy}
            disabled={busy}
          />
        </div>

        <PasswordRequirements value={password} id={PASSWORD_RULES_ID} />

        {error !== null && (
          <div
            id={errorId}
            role="alert"
            aria-live="polite"
            style={{
              padding: '10px 12px',
              borderRadius: 8,
              background: 'rgba(220, 38, 38, 0.08)',
              border: '1px solid rgba(220, 38, 38, 0.3)',
              color: '#991B1B',
              fontSize: 13,
              lineHeight: 1.45,
            }}
          >
            {error}
          </div>
        )}

        {/* ── CHK-01: the existing-account offer ──────────────────────────────
            Only ever rendered after the server has told us the address is
            taken, and only for THAT address. It sends a real branded message
            through the same auth-mail hook every other Elreign email goes
            through, and it says what it sent. A failure replaces the offer with
            the reason, because a screen that claims a send it did not make is
            the whole reason this row exists. */}
        {takenEmail !== null && (
          <div
            data-testid="signup-existing-account"
            style={{
              padding: '12px 14px',
              borderRadius: 9,
              background: 'var(--surface-2)',
              border: '1px solid var(--hairline-2)',
              fontSize: 13,
              color: 'var(--ink-2)',
              lineHeight: 1.5,
            }}
          >
            {recovery === 'sent' ? (
              <span data-testid="signup-existing-sent" role="status">
                Sent. Open {takenEmail} and follow the link to set a new password
                and sign in.
              </span>
            ) : (
              <>
                <span style={{ display: 'block', marginBottom: 9 }}>
                  Cannot remember the password for {takenEmail}? We can email you
                  a link to set a new one.
                </span>
                <button
                  type="button"
                  data-testid="signup-existing-send"
                  onClick={() => void sendRecoveryToTakenEmail()}
                  disabled={recovery === 'sending'}
                  className="btn-secondary"
                  style={{ fontSize: 13 }}
                >
                  {recovery === 'sending' ? 'Sending…' : 'Email me a link'}
                </button>
                {recoveryError !== null && (
                  <span
                    role="alert"
                    data-testid="signup-existing-error"
                    style={{ display: 'block', marginTop: 9, color: 'var(--negative)' }}
                  >
                    {recoveryError}
                  </span>
                )}
              </>
            )}
          </div>
        )}

        {/* SGN-03: Back now sits here, beside Create account, instead of above
            the form. Submit stays first in the DOM so Enter still submits and
            the tab order reaches the primary action before the way out. */}
        <div style={{ display: 'flex', gap: 10, alignItems: 'stretch' }}>
          <button
            type="submit"
            disabled={busy}
            style={{
              flex: 1,
              padding: '13px 18px',
              borderRadius: 10,
              border: 'none',
              background: busy ? 'var(--accent-200)' : 'var(--brand-accent, var(--accent-400))',
              color: 'var(--primary-900)',
              fontFamily: 'var(--sans)',
              fontSize: 15,
              fontWeight: 600,
              cursor: busy ? 'wait' : 'pointer',
              opacity: busy ? 0.7 : 1,
              transition: 'background 160ms var(--ease-cushion), opacity 160ms var(--ease-cushion)',
            }}
          >
            {busy ? 'Creating your account…' : 'Create account'}
          </button>

          {/* No persona step to return to on a firm self-signup, so no Back. */}
          {!isFirmSelfSignup && (
            <button
              type="button"
              onClick={backToPersona}
              disabled={busy}
              style={{
                flex: '0 0 auto',
                display: 'inline-flex',
                alignItems: 'center',
                gap: 6,
                padding: '13px 16px',
                borderRadius: 10,
                border: '1px solid var(--hairline-2)',
                background: 'var(--bg)',
                color: 'var(--ink-2)',
                fontFamily: 'var(--sans)',
                fontSize: 14,
                fontWeight: 500,
                cursor: busy ? 'default' : 'pointer',
                opacity: busy ? 0.6 : 1,
              }}
            >
              <span aria-hidden="true" style={{ fontSize: 15, lineHeight: 1 }}>
                ←
              </span>
              Back
            </button>
          )}
        </div>

        {/* SGN-03: the sign-in offer joined the terms line instead of sitting
            below the card as a paragraph of its own. Both are the same size and
            the same colour, both are things you read once, and as two blocks
            they cost 80px of the height Paul was scrolling past. The link is
            unchanged and still the same route. */}
        <p style={{ fontSize: 12.5, color: 'var(--ink-3)', margin: '2px 0 0', lineHeight: 1.55 }}>
          By continuing you agree to the{' '}
          <Link to="/legal/terms" style={{ color: 'var(--brand-primary, var(--primary-600))' }}>
            Terms and Conditions
          </Link>{' '}
          and{' '}
          <Link to="/legal/privacy" style={{ color: 'var(--brand-primary, var(--primary-600))' }}>
            Privacy Policy
          </Link>
          . Already have an account?{' '}
          <Link
            to="/auth/login"
            style={{ color: 'var(--brand-primary, var(--primary-600))', fontWeight: 500 }}
          >
            Sign in
          </Link>
          .
        </p>
      </form>
    </AuthLayout>
  );
}

/* ── Persona chooser ──────────────────────────────────────────────────────────
 *
 * Two large selectable cards (a WAI-ARIA radiogroup) shown on Step 1. Picking a
 * card sets the persona AND advances to Step 2 (via `onChoose`). The choice maps
 * directly to the backend `accountType` seam:
 *   - 'individual' → tenant kind 'b2c' → /onboarding/welcome (personal-trust
 *     wizard).
 *   - 'firm'       → tenant kind 'b2b' → /onboarding/firm (document-first
 *     book-of-many bootstrap).
 * The routing itself happens in /auth/callback off user_metadata.account_type;
 * this control only captures the choice. Keyboard: arrow keys move the active
 * card; Space/Enter selects (and advances).
 */
const PERSONAS: ReadonlyArray<{
  value: AccountType;
  title: string;
  desc: string;
  icon: React.ReactNode;
}> = [
  {
    value: 'individual',
    title: 'Individual',
    desc: 'Set up and manage my own trust.',
    icon: <IconPerson />,
  },
  {
    value: 'firm',
    title: 'Firm',
    desc: 'Manage trusts on behalf of clients.',
    icon: <IconFirm />,
  },
];

function PersonaChooser({
  value,
  onChoose,
}: {
  value: AccountType | null;
  onChoose: (next: AccountType) => void;
}) {
  // The active (roving-tabindex) card. Defaults to the previously-chosen
  // persona, else the first card, so re-entering Step 1 lands focus sensibly.
  const activeIdx = Math.max(
    0,
    PERSONAS.findIndex((p) => p.value === (value ?? PERSONAS[0]!.value)),
  );

  function handleKeyDown(e: React.KeyboardEvent, idx: number) {
    if (e.key === 'ArrowRight' || e.key === 'ArrowDown') {
      e.preventDefault();
      onChoose(PERSONAS[(idx + 1) % PERSONAS.length]!.value);
    } else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') {
      e.preventDefault();
      onChoose(PERSONAS[(idx - 1 + PERSONAS.length) % PERSONAS.length]!.value);
    } else if (e.key === ' ' || e.key === 'Enter') {
      e.preventDefault();
      onChoose(PERSONAS[idx]!.value);
    }
  }

  return (
    <div>
      <span
        id="persona-label"
        style={{ display: 'block', fontSize: 13, fontWeight: 500, color: 'var(--ink-2)', marginBottom: 10 }}
      >
        Choose which account you wish to create.
      </span>
      <div
        role="radiogroup"
        aria-labelledby="persona-label"
        style={{ display: 'flex', flexDirection: 'column', gap: 12 }}
      >
        {PERSONAS.map((p, idx) => {
          const selected = value === p.value;
          return (
            <div
              key={p.value}
              role="radio"
              aria-checked={selected}
              aria-label={p.title}
              tabIndex={idx === activeIdx ? 0 : -1}
              onClick={() => onChoose(p.value)}
              onKeyDown={(e) => handleKeyDown(e, idx)}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 14,
                padding: '18px 18px',
                borderRadius: 12,
                cursor: 'pointer',
                background: selected ? 'var(--primary-50)' : 'var(--surface-2)',
                border: selected ? '1.5px solid var(--brand-primary, var(--primary-600))' : '1.5px solid var(--hairline-2)',
                boxShadow: selected ? '0 0 0 3px rgba(13,109,107,0.10)' : 'none',
                transition:
                  'border-color 140ms var(--ease-cushion), background 140ms var(--ease-cushion), box-shadow 140ms var(--ease-cushion)',
                outline: 'none',
              }}
            >
              <span
                style={{
                  flex: '0 0 auto',
                  width: 40,
                  height: 40,
                  borderRadius: 10,
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  background: selected ? 'var(--brand-primary, var(--primary-600))' : 'var(--bg)',
                  border: selected ? 'none' : '1px solid var(--hairline-2)',
                  color: selected ? 'var(--surface, #fff)' : 'var(--ink-3)',
                }}
              >
                <span style={{ width: 22, height: 22, display: 'inline-flex' }}>{p.icon}</span>
              </span>
              <span style={{ display: 'flex', flexDirection: 'column', gap: 3, flex: 1 }}>
                <span style={{ fontSize: 15.5, fontWeight: 600, color: 'var(--ink)', lineHeight: 1.3 }}>
                  {p.title}
                </span>
                <span style={{ fontSize: 13, color: 'var(--ink-3)', lineHeight: 1.45 }}>{p.desc}</span>
              </span>
              <span
                aria-hidden="true"
                style={{
                  flex: '0 0 auto',
                  color: selected ? 'var(--brand-primary, var(--primary-600))' : 'var(--ink-4, var(--ink-3))',
                  fontSize: 18,
                  lineHeight: 1,
                }}
              >
                →
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

/** Single-person glyph — the Individual persona. */
function IconPerson() {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.7}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      style={{ width: '100%', height: '100%' }}
    >
      <circle cx="12" cy="8" r="3.4" />
      <path d="M5.5 19c.6-3.2 3.2-5 6.5-5s5.9 1.8 6.5 5" />
    </svg>
  );
}

/** Building / practice glyph — the Firm persona. */
function IconFirm() {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.7}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      style={{ width: '100%', height: '100%' }}
    >
      <path d="M4 20V6.2a1 1 0 0 1 .7-.95l7-2.2a1 1 0 0 1 1.3.95V20" />
      <path d="M13 9.5l6 1.9a1 1 0 0 1 .7.95V20" />
      <path d="M3 20h18" />
      <path d="M7 8.5h2.5M7 12h2.5M7 15.5h2.5M16 14h1.5M16 17h1.5" />
    </svg>
  );
}

/* ── Password field with show/hide toggle ─────────────────────────────────────
 *
 * A labelled password input with an eye toggle that flips the input between
 * `type="password"` and `type="text"`. The toggle is a `<button type="button">`
 * so it never submits the form, and it carries an `aria-label` that flips
 * between "Show password"/"Hide password" plus `aria-pressed` reflecting the
 * revealed state. Positioned inside the field (absolute, right) over a padded
 * input so the typed value never sits under the icon. Light theme + the shared
 * focus ring, matching the other inputs.
 *
 * Scoped to signup for this pass; login/reset/mfa are intentionally untouched.
 */
function PasswordField({
  id,
  label,
  hint,
  requirementsId,
  value,
  onChange,
  placeholder,
  autoComplete,
  invalid,
  describedBy,
  disabled,
}: {
  id: string;
  label: string;
  hint?: string;
  /**
   * Pass an id to draw the live rule checklist under this field (SGN-02). The
   * confirm field deliberately leaves it off: the rules are shown once, and
   * repeating them under both boxes reads as nagging.
   */
  requirementsId?: string;
  value: string;
  onChange: (v: string) => void;
  placeholder: string;
  autoComplete: string;
  invalid: boolean;
  describedBy?: string;
  disabled?: boolean;
}) {
  const [revealed, setRevealed] = useState(false);
  const describers = [requirementsId, describedBy].filter(Boolean).join(' ');

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
      <FieldLabel label={label} hint={hint} htmlFor={id}>
        <span style={{ position: 'relative', display: 'block' }}>
          <input
            id={id}
            type={revealed ? 'text' : 'password'}
            value={value}
            onChange={(e) => onChange(e.target.value)}
            placeholder={placeholder}
            autoComplete={autoComplete}
            required
            aria-invalid={invalid}
            aria-describedby={describers.length > 0 ? describers : undefined}
            disabled={disabled}
            minLength={PASSWORD_MIN_LENGTH}
            style={{ ...inputStyle, paddingRight: 44 }}
          />
          {/* Non-submitting toggle. Relies on the global `:focus-visible`
              outline (index.css) for its keyboard focus ring — same treatment as
              every other focusable control — so no inline outline override. */}
          <button
            type="button"
            onClick={() => setRevealed((v) => !v)}
            disabled={disabled}
            aria-label={revealed ? 'Hide password' : 'Show password'}
            aria-pressed={revealed}
            aria-controls={id}
            title={revealed ? 'Hide password' : 'Show password'}
            style={{
              position: 'absolute',
              top: 0,
              right: 0,
              height: '100%',
              width: 40,
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              background: 'none',
              border: 'none',
              padding: 0,
              margin: 0,
              color: 'var(--ink-3)',
              cursor: disabled ? 'default' : 'pointer',
              borderRadius: 9,
            }}
          >
            {revealed ? <IconEyeOff /> : <IconEye />}
          </button>
        </span>
      </FieldLabel>
      {requirementsId !== undefined && (
        <PasswordRequirements value={value} id={requirementsId} />
      )}
    </div>
  );
}

/** Open-eye glyph — password is hidden, click to reveal. */
function IconEye() {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.7}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      style={{ width: 19, height: 19 }}
    >
      <path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7-10-7-10-7Z" />
      <circle cx="12" cy="12" r="3" />
    </svg>
  );
}

/** Struck-through eye glyph — password is revealed, click to hide. */
function IconEyeOff() {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.7}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      style={{ width: 19, height: 19 }}
    >
      <path d="M9.9 5.2A9.5 9.5 0 0 1 12 5c6.5 0 10 7 10 7a16 16 0 0 1-2.6 3.4M6.6 6.6A16 16 0 0 0 2 12s3.5 7 10 7a9.5 9.5 0 0 0 4.1-.9" />
      <path d="M9.9 9.9a3 3 0 0 0 4.2 4.2" />
      <path d="M3 3l18 18" />
    </svg>
  );
}

function FieldLabel({
  label,
  hint,
  htmlFor,
  children,
}: {
  label: string;
  hint?: string;
  htmlFor?: string;
  children: React.ReactNode;
}) {
  return (
    <label htmlFor={htmlFor} style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
      <span style={{ fontSize: 13, fontWeight: 500, color: 'var(--ink-2)' }}>{label}</span>
      {children}
      {hint && (
        <span style={{ fontSize: 12, color: 'var(--ink-3)', marginTop: -2 }}>{hint}</span>
      )}
    </label>
  );
}

const inputStyle: React.CSSProperties = {
  width: '100%',
  padding: '11px 14px',
  borderRadius: 9,
  border: '1px solid var(--hairline-2)',
  background: 'var(--bg)',
  color: 'var(--ink)',
  fontFamily: 'var(--sans)',
  fontSize: 15,
  outline: 'none',
  boxSizing: 'border-box',
};
